import PrimaryButton from '@components/atoms/buttons/PrimaryButton';
import Hr from '@components/atoms/Hr';
import Section from '@components/atoms/Section';
import Spacing from '@components/atoms/Spacing';
import { Heading1, Heading3, Paragraph } from '@components/atoms/Typography';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import BREAKPOINTS from '@lib/constants/breakpoints';
import COLORS from '@lib/constants/colors';
import LINKS from '@lib/constants/links';
import ROUTES from '@lib/constants/routes';
import stats from '@lib/data/stats';
import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

interface Props {
  year: string;
  brand: string;
  model: string;
  vehicle: any;
}

const Aside = styled.aside`
  flex: 0 0 285px;
  max-width: 285px;
  margin-left: 70px;

  ol {
    li {
      margin-bottom: 8px;

      p {
        span {
          display: inline-block;
          margin-right: 4px;
        }
      }
    }
  }

  @media screen and (max-width: ${BREAKPOINTS.desk}) {
    max-width: none;
    margin: 0;
    flex: 1;
    padding: 32px;
  }
`;

const InnerAside = styled.div`
  transform: translateZ(0);
  padding-right: 10px;
  padding-top: 50px;
  position: sticky;
  top: 100px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    position: initial;
    padding: 0;
  }
`;

const Wrapper = styled.div`
  margin-right: auto;
  margin-left: auto;
  padding-right: 15px;
  padding-left: 15px;
  display: flex;

  background-color: #fff;
  padding: 32px;
  border-radius: 6px;

  @media screen and (max-width: ${BREAKPOINTS.desk}) {
    flex-wrap: wrap;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 0;
  }
`;

const TableSection = styled(Section)`
  padding-top: 16px;
  padding-bottom: 16px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding: 16px 0;
  }
`;

const Table = styled.table`
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;

  tr:nth-of-type(odd) {
    background: ${COLORS.lightGray};
  }

  tbody td,
  tbody th {
    padding: 5px;
  }

  th {
    background: ${COLORS.black};
    color: #fff;
    text-align: left;
  }

  td {
    padding: 5px;
  }

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    tr td:first-child {
      font-weight: bold;
    }

    th,
    td {
      display: block;
    }

    td:before {
      content: attr(data-key);
      display: block;
      font-size: 12px;
      color: #999;
      text-transform: uppercase;
    }
  }
`;
const Header = styled(Section)`
  padding-top: 120px;

  @media screen and (max-width: ${BREAKPOINTS.medium}) {
    padding-top: 50px;
  }
`;

const Page = ({ model, brand, year, vehicle }: Props) => {
  const router = useRouter();

  if (router.isFallback) return <PageContainer loading />;

  const links = [
    {
      label: 'Luna',
      href: '/',
    },
    {
      label: 'Ficha técnica',
      href: `${ROUTES.stats.home}`,
    },
    {
      label: 'Motos',
      href: `${ROUTES.stats.type({ type: 'motos' })}`,
    },
    {
      label: `${brand}`,
      href: `${ROUTES.stats.brand({ brand, type: 'motos' })}`,
    },

    {
      label: `${model}`,
      href: `${ROUTES.stats.model({ brand, model, type: 'motos' })}`,
    },

    {
      label: `${year}`,
      href: `${ROUTES.stats.yearBikeStats({
        brand,
        model,
        type: 'motos',
        year,
      })}`,
    },
  ];

  return (
    <PageContainer
      customAmpPage
      title={`Ficha técnica ${brand} ${model} ${year}`}
      metaDescription={`Ficha técnica com as especificações completas da ${brand} ${model} ${year}. Veja detalhes de motor, performance, valores, seguro e muito mais nesse guia!`}
    >
      <Wrapper>
        <div>
          <Header>
            <Heading1>
              Ficha técnica
              {` ${brand} ${model} ${year}`}
            </Heading1>
          </Header>
          <TableSection>
            <Table>
              <tbody>
                {Object.keys(vehicle).map((key) => (
                  <React.Fragment key={key}>
                    <tr>
                      <th colSpan={3}>
                        <strong>{key}</strong>
                      </th>
                    </tr>
                    {Object.entries({ ...vehicle[key] }).map((entry: any) => (
                      <tr key={entry[0]}>
                        <td>{entry[0]}</td>
                        <td>{entry[1]}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </TableSection>
        </div>

        <Aside>
          <InnerAside>
            <Heading3 center>
              Seguro para
              {` ${brand} ${model} ${year}`} Barato
            </Heading3>
            <Spacing top={2} bottom={4}>
              <Paragraph center>
                Veja o preço do seguro para a {` ${brand} ${model} ${year} `}
                em menos de 2 minutos
              </Paragraph>
            </Spacing>
            <Link href={ROUTES.startForm()}>
              <a>
                <PrimaryButton pink isBlock>
                  Ver Preços
                </PrimaryButton>
              </a>
            </Link>
            <Hr />
            <Spacing bottom={4}>
              <Heading3 center>
                Quer comprar uma {` ${brand} ${model} ${year}`}?
              </Heading3>
            </Spacing>

            <ol style={{ marginBottom: 32 }}>
              <li>
                <Paragraph>
                  <span>1.</span>Descubra as melhores ofertas disponíveis
                </Paragraph>
              </li>
              <li>
                <Paragraph>
                  <span>2.</span>Análise dos veículos pelos nossos consultores
                </Paragraph>
              </li>

              <li>
                <Paragraph>
                  <span>3.</span>Ferramenta 100% gratuita
                </Paragraph>
              </li>
            </ol>

            <a href={LINKS.carToolAirtable} target="_blank" rel="noreferrer">
              <PrimaryButton isBlock>Receber grátis</PrimaryButton>
            </a>
          </InnerAside>
        </Aside>
      </Wrapper>

      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs links={links} />
      </Section>
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const brand = params?.brand?.toString() as string;
  const model = params?.model?.toString() as string;
  const year = params?.year?.toString() as string;

  const vehicle = await stats.getVehicle(brand, model, year, 'motorcycle');

  if (!vehicle) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      model,
      brand,
      year,
      vehicle,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: ['/ficha-tecnica/motos/Honda/PCX150/2020'],
  fallback: true,
});

export default Page;
